"use client"

import React, { useState, useEffect, useLayoutEffect, useContext, useMemo, useRef, forwardRef } from "react"
import {
  Box,
  Grid,
  TextField,
  Button,
  Autocomplete,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  alpha,
  Checkbox,
  Collapse,
} from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ComposedChart,
} from "recharts"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import ReceiptIcon from "@mui/icons-material/Receipt"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import InfoIcon from "@mui/icons-material/Info"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import BarChartIcon from "@mui/icons-material/BarChart"
import PieChartIcon from "@mui/icons-material/PieChart"
import ShowChartIcon from "@mui/icons-material/ShowChart"
import FilterListIcon from "@mui/icons-material/FilterList"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import { ErpContext } from "../../context/ErpContext.js"
import { getLocaleConfig, formatNumberForDisplay } from "../../utils/numeros"

// ✅ Configuración de dayjs para manejo de timezone
dayjs.extend(utc)
dayjs.extend(timezone)

const TIPOS_ANALITICOS_IDS = [1, 5, 9]

// Tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
  },
})

/**
 * Listbox para Autocomplete múltiple con "Seleccionar todos" / "Seleccionar ninguno".
 * onMouseDown en la barra evita que el input pierda el foco (patrón MUI).
 */
function createAutocompleteListboxWithBulkActions(onSelectAll, onSelectNone) {
  const Listbox = forwardRef(function AutocompleteListboxBulk(props, ref) {
    const { children, ...other } = props
    return (
      <Box
        sx={{
          maxHeight: "min(40vh, 320px)",
          overflow: "auto",
          p: 0,
          m: 0,
        }}
      >
        <Box
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            alignItems: "center",
            px: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onSelectAll()
            }}
            sx={{ textTransform: "none", minWidth: "auto", px: 0.75, fontSize: "0.8125rem" }}
          >
            Seleccionar todos
          </Button>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onSelectNone()
            }}
            sx={{ textTransform: "none", minWidth: "auto", px: 0.75, fontSize: "0.8125rem" }}
          >
            Seleccionar ninguno
          </Button>
        </Box>
        <Box component="ul" ref={ref} {...other} sx={{ p: 0, m: 0, listStyle: "none" }}>
          {children}
        </Box>
      </Box>
    )
  })
  Listbox.displayName = "AutocompleteListboxBulk"
  return Listbox
}

/** Etiqueta para opciones ítem+ubicación (getItemAndItemUbicacion) */
function formatItemUbicacionOptionLabel(row) {
  const cod = (row.codigo || "").trim()
  const desc = (row.descripcion || "").trim()
  const ubi = (row.ubicacion || "").trim()
  const core = cod && desc ? `${cod} — ${desc}` : cod || desc || `Ítem ${row.idItem ?? ""}`
  return ubi ? `${core} · ${ubi}` : core
}

function formatEntidadOptionLabel(option) {
  const nom = (option?.descripcion || "").trim()
  const ap = (option?.apellido || "").trim()
  return nom && ap ? `${nom} ${ap}` : nom || ap || `Entidad ${option?.id ?? ""}`
}

const getUbicacionKey = (ubicacion) => ubicacion.id
const getUbicacionLabel = (ubicacion) => ubicacion.descripcion
const getTipoTransaccionKey = (tipo) => tipo.id
const getTipoTransaccionLabel = (tipo) => tipo.descripcion
const getEntidadKey = (entidad) => entidad.id
const getEntidadLabel = (entidad) => formatEntidadOptionLabel(entidad)
const getItemUbicacionKey = (item) => `${item.idItem}-${item.idUbicacion}`
const getItemUbicacionLabel = (item) => formatItemUbicacionOptionLabel(item)

function createCompactChipSx(backgroundColor, textColor) {
  return {
    backgroundColor,
    color: textColor,
    fontWeight: 500,
    maxWidth: 130,
    height: 26,
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      px: 1,
    },
    "& .MuiChip-deleteIcon": {
      color: alpha(textColor, 0.85),
      "&:hover": {
        color: textColor,
      },
    },
  }
}

const ubicacionChipSx = createCompactChipSx(
  alpha(theme.palette.secondary.main, 0.14),
  theme.palette.secondary.main
)

const tipoChipSx = createCompactChipSx(
  alpha(theme.palette.primary.main, 0.1),
  theme.palette.primary.main
)

const entidadChipSx = createCompactChipSx(
  alpha(theme.palette.info.main, 0.1),
  theme.palette.info.main
)

const itemChipSx = createCompactChipSx(
  alpha(theme.palette.success.main, 0.12),
  theme.palette.success.dark
)

const compactMoreChipSx = {
  flexShrink: 0,
  fontWeight: 500,
  height: 26,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: "text.secondary",
}

function estimateMoreChipWidth(hiddenCount) {
  return 32 + String(hiddenCount).length * 8
}

function calculateVisibleChipCountFromWidths(widths, availableWidth) {
  if (!Array.isArray(widths) || widths.length === 0) return 0
  if (!availableWidth || Number.isNaN(availableWidth)) return Math.min(widths.length, 2)

  const gap = 6
  let usedWidth = 0
  let visibleCount = 0

  for (let index = 0; index < widths.length; index += 1) {
    const chipWidth = widths[index] + (visibleCount > 0 ? gap : 0)
    const hiddenCount = widths.length - (index + 1)
    const moreChipWidth =
      hiddenCount > 0 ? gap + estimateMoreChipWidth(hiddenCount) : 0

    if (usedWidth + chipWidth + moreChipWidth <= availableWidth) {
      usedWidth += chipWidth
      visibleCount += 1
      continue
    }

    break
  }

  return Math.max(1, visibleCount)
}

function getCompactAutocompleteSx(inputValue) {
  return {
    minWidth: 0,
    "& .MuiOutlinedInput-root": {
      minHeight: 40,
    },
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
      overflow: "hidden",
      alignItems: "center",
    },
    "& .MuiAutocomplete-input": {
      minWidth: String(inputValue || "").trim() ? 34 : 12,
      width: String(inputValue || "").trim() ? "auto" : "12px",
    },
  }
}

function renderCompactChipSelection(value, getTagProps, getLabel, getKey, visibleCount, chipSx) {
  if (!Array.isArray(value) || value.length === 0) return null

  const visibleItems = value.slice(0, visibleCount)
  const hiddenCount = Math.max(0, value.length - visibleItems.length)

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
    >
      {visibleItems.map((item, index) => (
        <Chip
          key={getKey(item)}
          label={getLabel(item)}
          {...getTagProps({ index })}
          size="small"
          sx={chipSx}
        />
      ))}
      {hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount}`}
          size="small"
          sx={compactMoreChipSx}
        />
      )}
    </Box>
  )
}

function renderHiddenMeasurementChips(values, measureRefs, getKey, getLabel, chipSx) {
  return values.map((value) => (
    <Chip
      key={`measure-${getKey(value)}`}
      label={getLabel(value)}
      size="small"
      onDelete={() => {}}
      ref={(node) => {
        if (node) {
          measureRefs.current.set(getKey(value), node)
        } else {
          measureRefs.current.delete(getKey(value))
        }
      }}
      sx={chipSx}
    />
  ))
}

function useCompactMultiSelectLayout(selectedValues, inputValue, getKey) {
  const [visibleChipCount, setVisibleChipCount] = useState(2)
  const fieldRef = useRef(null)
  const measureRefs = useRef(new Map())

  useLayoutEffect(() => {
    const fieldElement = fieldRef.current
    const inputRoot = fieldElement?.querySelector(".MuiAutocomplete-inputRoot")

    if (!fieldElement || !inputRoot) {
      setVisibleChipCount(Math.min(selectedValues.length, 2))
      return undefined
    }

    const updateVisibleChips = () => {
      const endAdornmentWidth =
        fieldElement.querySelector(".MuiAutocomplete-endAdornment")?.getBoundingClientRect().width || 0
      const inputWidthReal = fieldElement.querySelector(".MuiAutocomplete-input")?.getBoundingClientRect().width || 0
      const reservedInputWidth = String(inputValue || "").trim()
        ? Math.max(34, Math.min(inputWidthReal || 34, 120))
        : 14
      const rootStyles = window.getComputedStyle(inputRoot)
      const paddingLeft = Number.parseFloat(rootStyles.paddingLeft) || 0
      const paddingRight = Number.parseFloat(rootStyles.paddingRight) || 0
      const availableWidth =
        inputRoot.getBoundingClientRect().width - paddingLeft - paddingRight - endAdornmentWidth - reservedInputWidth

      const measuredWidths = selectedValues.map((value) => {
        return measureRefs.current.get(getKey(value))?.getBoundingClientRect().width || 0
      })

      const nextVisibleCount = calculateVisibleChipCountFromWidths(measuredWidths, availableWidth)
      setVisibleChipCount((currentValue) =>
        currentValue === nextVisibleCount ? currentValue : nextVisibleCount
      )
    }

    updateVisibleChips()

    if (typeof ResizeObserver === "undefined") {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      updateVisibleChips()
    })

    observer.observe(fieldElement)
    observer.observe(inputRoot)

    return () => {
      observer.disconnect()
    }
  }, [selectedValues, inputValue, getKey])

  return { fieldRef, measureRefs, visibleChipCount }
}

const CajaComponent = () => {
  const {
    getCajaByMedioDePago,
    getMedioDePago,
    tipoMedioDePago,
    medioDePagoIdTipoEspecifico,
    getTipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    ubicaciones,
    getUbicacion,
    getFechaHoraActualClient, // ✅ Para normalizar fechas con timezone
    formatoNumericoGlobal, // ✅ Para formateo numérico regional
    getTipoTransaccion,
    tipoTransaccion,
    getEntidad,
    getItemAndItemUbicacion,
  } = useContext(ErpContext)

  // ✅ Configuración de locale para formateo numérico (según CONFIGURACION-NUMERICA.md)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Fechas del mes actual (strings para los inputs type="date")
  const today = dayjs()
  const firstDay = today.startOf("month").format("YYYY-MM-DD")
  const lastDay = today.endOf("month").format("YYYY-MM-DD")

  const [startDate, setStartDate] = useState(firstDay)
  const [endDate, setEndDate] = useState(lastDay)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState(null)
  const [selectedUbicaciones, setSelectedUbicaciones] = useState([])
  const [movimientos, setMovimientos] = useState([])
  const [caja, setCaja] = useState([])
  const [loading, setLoading] = useState(false)
  
  // Nuevos estados para filtros avanzados
  const [selectedTiposTransaccion, setSelectedTiposTransaccion] = useState([])
  const [selectedEntidades, setSelectedEntidades] = useState([])
  const [entidadesDisponibles, setEntidadesDisponibles] = useState([])
  const [loadingEntidades, setLoadingEntidades] = useState(false)
  const [showFiltrosAvanzados, setShowFiltrosAvanzados] = useState(false)
  const [itemUbicacionOptions, setItemUbicacionOptions] = useState([])
  const [loadingItemUbicacionOptions, setLoadingItemUbicacionOptions] = useState(false)
  const [selectedItemsUbicacion, setSelectedItemsUbicacion] = useState([])
  const [ubicacionesInputValue, setUbicacionesInputValue] = useState("")
  const [tiposTransaccionInputValue, setTiposTransaccionInputValue] = useState("")
  const [entidadesInputValue, setEntidadesInputValue] = useState("")
  const [itemsInputValue, setItemsInputValue] = useState("")
  const tiposTransaccionAnaliticos = useMemo(
    () => tipoTransaccion.filter((tipo) => TIPOS_ANALITICOS_IDS.includes(tipo.id)),
    [tipoTransaccion]
  )
  const ubicacionesCompactLayout = useCompactMultiSelectLayout(
    selectedUbicaciones,
    ubicacionesInputValue,
    getUbicacionKey
  )
  const tiposCompactLayout = useCompactMultiSelectLayout(
    selectedTiposTransaccion,
    tiposTransaccionInputValue,
    getTipoTransaccionKey
  )
  const entidadesCompactLayout = useCompactMultiSelectLayout(
    selectedEntidades,
    entidadesInputValue,
    getEntidadKey
  )
  const itemsCompactLayout = useCompactMultiSelectLayout(
    selectedItemsUbicacion,
    itemsInputValue,
    getItemUbicacionKey
  )

  const ListboxUbicaciones = useMemo(
    () =>
      createAutocompleteListboxWithBulkActions(
        () => setSelectedUbicaciones([...ubicaciones]),
        () => setSelectedUbicaciones([])
      ),
    [ubicaciones]
  )

  const ListboxTiposTransaccion = useMemo(
    () =>
      createAutocompleteListboxWithBulkActions(
        () => setSelectedTiposTransaccion([...tiposTransaccionAnaliticos]),
        () => setSelectedTiposTransaccion([])
      ),
    [tiposTransaccionAnaliticos]
  )

  // Entidades e Ítems usan búsqueda bajo demanda (no bulk select)

  // === Búsqueda bajo demanda de Entidades (debounce 350ms) ===
  const entidadesTimerRef = useRef(null)
  const handleEntidadInputChange = (_event, value, reason) => {
    setEntidadesInputValue(value || "")
    if (reason !== "input") return
    if (entidadesTimerRef.current) clearTimeout(entidadesTimerRef.current)
    const trimmed = (value || "").trim()
    if (trimmed.length < 2) {
      setEntidadesDisponibles([])
      return
    }
    entidadesTimerRef.current = setTimeout(async () => {
      try {
        setLoadingEntidades(true)
        const response = await getEntidad(1, 50, trimmed, {}, null)
        const rows = response?.data ?? (Array.isArray(response) ? response : [])
        setEntidadesDisponibles(rows)
      } catch (e) {
        console.error("Error buscando entidades:", e)
      } finally {
        setLoadingEntidades(false)
      }
    }, 350)
  }

  // Opciones de entidades: mergear seleccionados + resultados de búsqueda (sin duplicados)
  const entidadesOptions = useMemo(() => {
    const map = new Map()
    for (const e of selectedEntidades) map.set(e.id, e)
    for (const e of entidadesDisponibles) { if (!map.has(e.id)) map.set(e.id, e) }
    return Array.from(map.values())
  }, [selectedEntidades, entidadesDisponibles])

  // === Búsqueda bajo demanda de Ítems por ubicación (debounce 350ms) ===
  const itemsTimerRef = useRef(null)
  const handleItemInputChange = (_event, value, reason) => {
    setItemsInputValue(value || "")
    if (reason !== "input") return
    if (itemsTimerRef.current) clearTimeout(itemsTimerRef.current)
    const trimmed = (value || "").trim()
    if (trimmed.length < 2) {
      setItemUbicacionOptions([])
      return
    }
    itemsTimerRef.current = setTimeout(async () => {
      try {
        setLoadingItemUbicacionOptions(true)
        const merge = new Map()
        // Siempre incluir los ya seleccionados
        for (const s of selectedItemsUbicacion) {
          merge.set(`${s.idItem}-${s.idUbicacion}`, s)
        }
        const ingestRows = (rows) => {
          if (!Array.isArray(rows)) return
          for (const row of rows) {
            if (!row || row.idItem == null || row.idUbicacion == null) continue
            const key = `${row.idItem}-${row.idUbicacion}`
            if (!merge.has(key)) merge.set(key, row)
          }
        }
        if (selectedUbicaciones.length > 0) {
          await Promise.all(
            selectedUbicaciones.map(async (u) => {
              const res = await getItemAndItemUbicacion(
                1, 50, u.id, null, null, false, false, false, null, trimmed, false, {}
              )
              ingestRows(Array.isArray(res?.data) ? res.data : [])
            })
          )
        } else {
          const res = await getItemAndItemUbicacion(
            1, 50, null, null, null, false, false, false, null, trimmed, false, {}
          )
          ingestRows(Array.isArray(res?.data) ? res.data : [])
        }
        const list = Array.from(merge.values()).sort((a, b) => {
          const ca = `${(a.codigo || "").toLowerCase()}${(a.descripcion || "").toLowerCase()}`
          const cb = `${(b.codigo || "").toLowerCase()}${(b.descripcion || "").toLowerCase()}`
          return ca.localeCompare(cb, "es")
        })
        setItemUbicacionOptions(list)
      } catch (e) {
        console.error("Error buscando ítems:", e)
        setItemUbicacionOptions([])
      } finally {
        setLoadingItemUbicacionOptions(false)
      }
    }, 350)
  }

  // Opciones de ítems: mergear seleccionados + resultados de búsqueda (sin duplicados)
  const itemsOptions = useMemo(() => {
    const map = new Map()
    for (const s of selectedItemsUbicacion) map.set(`${s.idItem}-${s.idUbicacion}`, s)
    for (const o of itemUbicacionOptions) {
      const key = `${o.idItem}-${o.idUbicacion}`
      if (!map.has(key)) map.set(key, o)
    }
    return Array.from(map.values())
  }, [selectedItemsUbicacion, itemUbicacionOptions])

  // Función para formatear fecha correctamente (evita problemas de zona horaria)
  const formatearFecha = (fechaString) => {
    if (!fechaString) return ""

    // Si la fecha viene en formato YYYY-MM-DD, crear la fecha directamente
    if (typeof fechaString === "string" && fechaString.includes("-")) {
      const [year, month, day] = fechaString.split("T")[0].split("-")
      const fecha = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
      return fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })
    }

    // Para otros formatos, usar el método estándar
    const fecha = new Date(fechaString)
    return fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })
  }

  // Función para formatear fecha completa
  const formatearFechaCompleta = (fechaString) => {
    if (!fechaString) return ""

    if (typeof fechaString === "string" && fechaString.includes("-")) {
      const [year, month, day] = fechaString.split("T")[0].split("-")
      const fecha = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
      return fecha.toLocaleDateString("es-AR")
    }

    const fecha = new Date(fechaString)
    return fecha.toLocaleDateString("es-AR")
  }

  // Función para extraer solo la fecha (YYYY-MM-DD) sin problemas de zona horaria
  const extraerFecha = (fechaString) => {
    if (!fechaString) return ""

    // Si ya es una fecha en formato YYYY-MM-DD, devolverla tal como está
    if (typeof fechaString === "string" && fechaString.includes("-")) {
      return fechaString.split("T")[0] // Remover la parte de tiempo si existe
    }

    // Si es un objeto Date, convertirlo a formato local
    const fecha = new Date(fechaString)
    const year = fecha.getFullYear()
    const month = String(fecha.getMonth() + 1).padStart(2, "0")
    const day = String(fecha.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  // === Carga inicial de datos ===
  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    await getMedioDePago()
    await getTipoMedioDePago()
    await getUbicacion()
    await getTipoTransaccion()

    await handleFiltrar()
  }

  // Preseleccionar ubicación con id = 1 por defecto
  useEffect(() => {
    if (ubicaciones.length > 0 && selectedUbicaciones.length === 0) {
      const ubicacionDefault = ubicaciones.find(ubic => ubic.id === 1)
      if (ubicacionDefault) {
        setSelectedUbicaciones([ubicacionDefault])
      }
    }
  }, [ubicaciones])

  // Al seleccionar categoría, cargo medios filtrados
  useEffect(() => {
    if (selectedTipoMedioDePago) {
      getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
    }
  }, [selectedTipoMedioDePago])

  useEffect(() => {
    setSelectedTiposTransaccion((prev) =>
      prev.filter((tipo) => TIPOS_ANALITICOS_IDS.includes(tipo.id))
    )
  }, [tipoTransaccion])

  const fetchAnaliticos = async (filters = {}) => {
    const {
      startDateValue = startDate,
      endDateValue = endDate,
      selectedMedioDePagoValue = selectedMedioDePago,
      selectedTipoMedioDePagoValue = selectedTipoMedioDePago,
      selectedUbicacionesValue = selectedUbicaciones,
      selectedTiposTransaccionValue = selectedTiposTransaccion,
      selectedEntidadesValue = selectedEntidades,
      selectedItemsUbicacionValue = selectedItemsUbicacion,
    } = filters

    try {
      setLoading(true)
      
      // ✅ Normalizar fechas con timezone según política FILTROS-FECHAS-Y-TIMEZONE.md
      // Esto garantiza que funcione igual en local (GMT-3) y producción (UTC)
      const resultStartDate = await getFechaHoraActualClient(startDateValue)
      const resultEndDate = await getFechaHoraActualClient(endDateValue)
      
      // Convertir a Date objects con timezone correcto
      const startDateWithTz = dayjs(resultStartDate.fechaServidor).startOf("day").toDate()
      const endDateWithTz = dayjs(resultEndDate.fechaServidor).endOf("day").toDate()
      
      const response = await getCajaByMedioDePago({
        startDate: startDateWithTz, // ✅ Date object con timezone
        endDate: endDateWithTz, // ✅ Date object con timezone
        medioDePagoId: selectedMedioDePagoValue ? selectedMedioDePagoValue.id : null,
        tipoMedioDePagoId: selectedTipoMedioDePagoValue ? selectedTipoMedioDePagoValue.id : null,
        ubicacionIds: selectedUbicacionesValue.length > 0 ? selectedUbicacionesValue.map(u => u.id) : null,
        tiposTransaccionIds: selectedTiposTransaccionValue.length > 0 ? selectedTiposTransaccionValue.map(t => t.id) : null,
        entidadesIds: selectedEntidadesValue.length > 0 ? selectedEntidadesValue.map(e => e.id) : null,
        itemUbicacionPairs:
          selectedItemsUbicacionValue.length > 0
            ? selectedItemsUbicacionValue.map((o) => `${o.idItem}_${o.idUbicacion}`).join(",")
            : null,
      })

      const { movimientos: movs, caja: cajaData } = response.data
      setMovimientos(movs)
      setCaja(cajaData)
    } catch (error) {
      console.error("Error al filtrar:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleFiltrar = async () => {
    await fetchAnaliticos()
  }

  const handleClearFilters = async () => {
    const defaultStartDate = firstDay
    const defaultEndDate = lastDay

    setStartDate(defaultStartDate)
    setEndDate(defaultEndDate)
    setSelectedTipoMedioDePago(null)
    setSelectedMedioDePago(null)
    setSelectedUbicaciones([])
    setUbicacionesInputValue("")
    setTiposTransaccionInputValue("")
    setSelectedTiposTransaccion([])
    setEntidadesInputValue("")
    setSelectedEntidades([])
    setEntidadesDisponibles([])
    setItemsInputValue("")
    setSelectedItemsUbicacion([])
    setItemUbicacionOptions([])

    await fetchAnaliticos({
      startDateValue: defaultStartDate,
      endDateValue: defaultEndDate,
      selectedMedioDePagoValue: null,
      selectedTipoMedioDePagoValue: null,
      selectedUbicacionesValue: [],
      selectedTiposTransaccionValue: [],
      selectedEntidadesValue: [],
      selectedItemsUbicacionValue: [],
    })
  }

  const esVenta = (movimiento) => {
    if (movimiento?.categoriaAnalitica) return movimiento.categoriaAnalitica === "venta"
    return Number(movimiento?.monto) > 0
  }

  const esGasto = (movimiento) => {
    if (movimiento?.categoriaAnalitica) return movimiento.categoriaAnalitica === "gasto"
    return Number(movimiento?.monto) < 0
  }


  // Cálculos y análisis de datos
  const analytics = useMemo(() => {
    if (!movimientos.length) return null

    const getMontoMovimiento = (movimiento) => {
      const monto = Number(movimiento?.monto) || 0
      return esGasto(movimiento) ? monto : Math.abs(monto)
    }

    const totalVentas = movimientos
      .filter((movimiento) => esVenta(movimiento))
      .reduce((sum, movimiento) => sum + getMontoMovimiento(movimiento), 0)
    const totalGastos = movimientos
      .filter((movimiento) => esGasto(movimiento))
      .reduce((sum, movimiento) => sum + getMontoMovimiento(movimiento), 0)
    const netoTotal = totalVentas - totalGastos

    // Análisis por tipo de transacción
    const porTipoTransaccion = movimientos.reduce((acc, mov) => {
      const tipo = mov.tipoTransaccion
      if (!acc[tipo]) {
        acc[tipo] = { ventas: 0, gastos: 0, cantidad: 0 }
      }
      if (esVenta(mov)) {
        acc[tipo].ventas += getMontoMovimiento(mov)
      } else if (esGasto(mov)) {
        acc[tipo].gastos += getMontoMovimiento(mov)
      }
      acc[tipo].cantidad += 1
      return acc
    }, {})

    // Análisis por fecha - CORREGIDO para evitar problemas de zona horaria
    const porFecha = movimientos.reduce((acc, mov) => {
      const fechaKey = extraerFecha(mov.fecha) // Usar la función corregida
      if (!acc[fechaKey]) {
        acc[fechaKey] = { ventas: 0, gastos: 0, transacciones: 0 }
      }
      if (esVenta(mov)) {
        acc[fechaKey].ventas += getMontoMovimiento(mov)
      } else if (esGasto(mov)) {
        acc[fechaKey].gastos += getMontoMovimiento(mov)
      }
      acc[fechaKey].transacciones += 1
      return acc
    }, {})

    // Promedio diario
    const diasConMovimientos = Object.keys(porFecha).length
    const promedioVentasDiario = diasConMovimientos > 0 ? totalVentas / diasConMovimientos : 0
    const promedioGastosDiario = diasConMovimientos > 0 ? totalGastos / diasConMovimientos : 0

    // Día con mayor movimiento
    const diaConMayorMovimiento = Object.entries(porFecha).reduce(
      (max, [fecha, datos]) => {
        const total = datos.ventas + datos.gastos
        return total > max.total ? { fecha, total, ...datos } : max
      },
      { fecha: "", total: 0, ventas: 0, gastos: 0, transacciones: 0 },
    )

    return {
      totalVentas,
      totalGastos,
      netoTotal,
      porTipoTransaccion,
      porFecha,
      promedioVentasDiario,
      promedioGastosDiario,
      diaConMayorMovimiento,
      diasConMovimientos,
      totalTransacciones: movimientos.length,
    }
  }, [movimientos])

  // ✅ Formatear moneda usando configuración regional (según CONFIGURACION-NUMERICA.md)
  const formatCurrency = (value) => {
    // Usar formatNumberForDisplay con el locale configurado globalmente
    const formattedNumber = formatNumberForDisplay(value, { locale: localeConfig.locale })
    return `$ ${formattedNumber}`
  }

  // Datos para gráficos (filtrar tipos con valor 0 para que no generen porciones vacías)
  const pieData = analytics
    ? Object.entries(analytics.porTipoTransaccion)
        .map(([tipo, datos]) => ({
          name: tipo,
          value: datos.ventas + datos.gastos,
          ventas: datos.ventas,
          gastos: datos.gastos,
          cantidad: datos.cantidad,
        }))
        .filter((d) => d.value > 0)
    : []

  // CORREGIDO: Datos para gráfico de líneas con fechas correctas
  const lineData = analytics
    ? Object.entries(analytics.porFecha)
        .map(([fechaKey, datos]) => ({
          fecha: formatearFecha(fechaKey), // Usar la función corregida
          fechaCompleta: fechaKey, // Mantener la fecha original para ordenamiento
          ventas: datos.ventas,
          gastos: datos.gastos,
          neto: datos.ventas - datos.gastos,
          transacciones: datos.transacciones,
        }))
        .sort((a, b) => new Date(a.fechaCompleta) - new Date(b.fechaCompleta)) // Ordenar por fecha completa
    : []

  // CORREGIDO: Datos para gráfico compuesto con fechas correctas
  const composedData = caja.map((item) => ({
    fecha: formatearFecha(item.fecha), // Usar la función corregida
    ventas: item.ventas || 0,
    gastos: item.gastos || 0,
    montoNeto: item.montoNeto,
    transacciones: item.transacciones || 0,
    fechaCompleta: extraerFecha(item.fecha), // Para debugging
  }))

  // Colores para gráficos
  const COLORS = [
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            left: -9999,
            top: -9999,
            whiteSpace: "nowrap",
          }}
        >
          {renderHiddenMeasurementChips(
            selectedUbicaciones,
            ubicacionesCompactLayout.measureRefs,
            getUbicacionKey,
            getUbicacionLabel,
            ubicacionChipSx
          )}
          {renderHiddenMeasurementChips(
            selectedTiposTransaccion,
            tiposCompactLayout.measureRefs,
            getTipoTransaccionKey,
            getTipoTransaccionLabel,
            tipoChipSx
          )}
          {renderHiddenMeasurementChips(
            selectedEntidades,
            entidadesCompactLayout.measureRefs,
            getEntidadKey,
            getEntidadLabel,
            entidadChipSx
          )}
          {renderHiddenMeasurementChips(
            selectedItemsUbicacion,
            itemsCompactLayout.measureRefs,
            getItemUbicacionKey,
            getItemUbicacionLabel,
            itemChipSx
          )}
        </Box>

        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            Análisis Financiero
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visualiza ventas y gastos del negocio con criterios analíticos unificados
          </Typography>
        </Paper>

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3, overflowX: { md: "auto" } }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "center",
              gridTemplateColumns: {
                xs: "1fr",
                md: "160px 160px minmax(240px, 1.35fr) minmax(170px, 1fr) minmax(170px, 1fr) auto auto auto",
              },
              minWidth: { md: 1120 },
            }}
          >
            <Box>
              <TextField
                type="date"
                label="Desde"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
              />
            </Box>
            <Box>
              <TextField
                type="date"
                label="Hasta"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }} ref={ubicacionesCompactLayout.fieldRef}>
              <Autocomplete
                multiple
                ListboxComponent={ListboxUbicaciones}
                options={ubicaciones}
                disableCloseOnSelect
                getOptionLabel={(option) => option.descripcion}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedUbicaciones}
                inputValue={ubicacionesInputValue}
                onInputChange={(_event, newInputValue) => {
                  setUbicacionesInputValue(newInputValue)
                }}
                onChange={(event, newValue) => {
                  setSelectedUbicaciones(newValue)
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<span style={{ width: 17, height: 17, border: '2px solid #ccc', borderRadius: 3 }} />}
                      checkedIcon={<span style={{ width: 17, height: 17, backgroundColor: theme.palette.secondary.main, border: '2px solid ' + theme.palette.secondary.main, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12 }}>✓</span>}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.descripcion}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                      label="Ubicaciones"
                      placeholder={selectedUbicaciones.length === 0 ? "Buscar ubicaciones..." : ""}
                      size="small"
                    />
                  )}
                renderTags={(value, getTagProps) =>
                  renderCompactChipSelection(
                    value,
                    getTagProps,
                    getUbicacionLabel,
                    getUbicacionKey,
                    ubicacionesCompactLayout.visibleChipCount,
                    ubicacionChipSx
                  )
                }
                sx={getCompactAutocompleteSx(ubicacionesInputValue)}
              />
            </Box>
            <Box>
              <Autocomplete
                options={tipoMedioDePago}
                getOptionLabel={(opt) => opt.descripcion}
                value={selectedTipoMedioDePago}
                onChange={(_, v) => setSelectedTipoMedioDePago(v)}
                renderInput={(params) => <TextField {...params} label="Categoría Medio" size="small" />}
              />
            </Box>
            <Box>
              <Autocomplete
                options={medioDePagoIdTipoEspecifico}
                getOptionLabel={(opt) => opt.descripcion}
                value={selectedMedioDePago}
                onChange={(_, v) => setSelectedMedioDePago(v)}
                renderInput={(params) => <TextField {...params} label="Medio de Pago" size="small" />}
                disabled={!selectedTipoMedioDePago}
              />
            </Box>
            <Box>
              <Button
                variant="outlined"
                onClick={() => setShowFiltrosAvanzados(!showFiltrosAvanzados)}
                size="medium"
                startIcon={<FilterListIcon />}
                endIcon={showFiltrosAvanzados ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.secondary.main,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  },
                }}
              >
                Más
              </Button>
            </Box>
            <Box>
              <Button
                variant="contained"
                onClick={handleFiltrar}
                size="medium"
                disabled={loading}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": { backgroundColor: theme.palette.secondary.main },
                }}
              >
                {loading ? "Cargando..." : "Filtrar"}
              </Button>
            </Box>
            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "center" } }}>
              <Tooltip title="Limpiar filtros">
                <IconButton
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                    borderRadius: 1.5,
                    p: 0.9,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.15),
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <FilterAltOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filtros Avanzados Colapsables */}
          <Collapse in={showFiltrosAvanzados}>
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={3}>
              {/* Filtro de Tipos de Transacción */}
              <Grid item xs={12} md={6} ref={tiposCompactLayout.fieldRef}>
              <Autocomplete
                multiple
                ListboxComponent={ListboxTiposTransaccion}
                options={tiposTransaccionAnaliticos}
                disableCloseOnSelect
                inputValue={tiposTransaccionInputValue}
                onInputChange={(_event, newInputValue) => {
                  setTiposTransaccionInputValue(newInputValue)
                }}
                getOptionLabel={(option) => option.descripcion}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedTiposTransaccion}
                onChange={(event, newValue) => {
                  setSelectedTiposTransaccion(newValue)
                }}
                renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<span style={{ width: 17, height: 17, border: '2px solid #ccc', borderRadius: 3 }} />}
                        checkedIcon={<span style={{ width: 17, height: 17, backgroundColor: theme.palette.primary.main, border: '2px solid ' + theme.palette.primary.main, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12 }}>✓</span>}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.descripcion}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipos de Transacción"
                      placeholder={selectedTiposTransaccion.length === 0 ? "Buscar tipos..." : ""}
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    renderCompactChipSelection(
                      value,
                      getTagProps,
                      getTipoTransaccionLabel,
                      getTipoTransaccionKey,
                      tiposCompactLayout.visibleChipCount,
                      tipoChipSx
                    )
                  }
                  sx={getCompactAutocompleteSx(tiposTransaccionInputValue)}
                />
              </Grid>

              {/* Filtro de Entidades */}
              <Grid item xs={12} md={6} ref={entidadesCompactLayout.fieldRef}>
              <Autocomplete
                multiple
                options={entidadesOptions}
                loading={loadingEntidades}
                disableCloseOnSelect
                filterOptions={(x) => x}
                onInputChange={handleEntidadInputChange}
                inputValue={entidadesInputValue}
                getOptionLabel={getEntidadLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedEntidades}
                onChange={(event, newValue) => {
                  setSelectedEntidades(newValue)
                }}
                noOptionsText="Escribí al menos 2 caracteres para buscar..."
                renderOption={(props, option, { selected }) => {
                    const label = getEntidadLabel(option)
                    return (
                      <li {...props}>
                        <Checkbox
                          icon={<span style={{ width: 17, height: 17, border: '2px solid #ccc', borderRadius: 3 }} />}
                          checkedIcon={<span style={{ width: 17, height: 17, backgroundColor: theme.palette.info.main, border: '2px solid ' + theme.palette.info.main, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12 }}>✓</span>}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {label}
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Entidades (Clientes/Proveedores)"
                      placeholder={selectedEntidades.length === 0 ? "Buscar entidades..." : ""}
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    renderCompactChipSelection(
                      value,
                      getTagProps,
                      getEntidadLabel,
                      getEntidadKey,
                      entidadesCompactLayout.visibleChipCount,
                      entidadChipSx
                    )
                  }
                  sx={getCompactAutocompleteSx(entidadesInputValue)}
                />
              </Grid>

              {/* Ítems (prefiltrados por ubicaciones seleccionadas en la barra superior) */}
              <Grid item xs={12} ref={itemsCompactLayout.fieldRef}>
                <Autocomplete
                  multiple
                  loading={loadingItemUbicacionOptions}
                  options={itemsOptions}
                  filterOptions={(x) => x}
                  onInputChange={handleItemInputChange}
                  inputValue={itemsInputValue}
                  disableCloseOnSelect
                  getOptionLabel={getItemUbicacionLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.idItem === value.idItem && option.idUbicacion === value.idUbicacion
                  }
                  value={selectedItemsUbicacion}
                  onChange={(event, newValue) => {
                    setSelectedItemsUbicacion(newValue)
                  }}
                  noOptionsText="Escribí al menos 2 caracteres para buscar..."
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<span style={{ width: 17, height: 17, border: "2px solid #ccc", borderRadius: 3 }} />}
                        checkedIcon={
                          <span
                            style={{
                              width: 17,
                              height: 17,
                              backgroundColor: theme.palette.success.main,
                              border: `2px solid ${theme.palette.success.main}`,
                              borderRadius: 3,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: 12,
                            }}
                          >
                            ✓
                          </span>
                        }
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {formatItemUbicacionOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ítems (código · descripción · ubicación)"
                      placeholder={
                        selectedItemsUbicacion.length === 0 ? "Buscar ítems..." : ""
                      }
                      size="small"
                      helperText={
                        selectedUbicaciones.length > 0
                          ? "Búsqueda acotada a las ubicaciones seleccionadas arriba."
                          : "Sin ubicaciones elegidas: busca en todas las ubicaciones."
                      }
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    renderCompactChipSelection(
                      value,
                      getTagProps,
                      getItemUbicacionLabel,
                      getItemUbicacionKey,
                      itemsCompactLayout.visibleChipCount,
                      itemChipSx
                    )
                  }
                  sx={getCompactAutocompleteSx(itemsInputValue)}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {analytics && (
          <>
            {/* Tarjetas de resumen principales */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <TrendingUpIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Total de transacciones de tipo Venta incluidas en el análisis">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Ventas
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(analytics.totalVentas)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      Promedio diario: {formatCurrency(analytics.promedioVentasDiario)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${theme.palette.error.main}, ${alpha(theme.palette.error.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <TrendingDownIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Gastos según TablaGastos: tipos 5 y 9, excluyendo pagos desde cuenta corriente">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Gastos
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(analytics.totalGastos)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      Promedio diario: {formatCurrency(analytics.promedioGastosDiario)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${analytics.netoTotal >= 0 ? theme.palette.info.main : theme.palette.warning.main}, ${alpha(analytics.netoTotal >= 0 ? theme.palette.info.main : theme.palette.warning.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Diferencia entre ventas y gastos (Ventas - Gastos)">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Balance Neto
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatCurrency(analytics.netoTotal)}
                    </Typography>
                    <Chip
                      label={analytics.netoTotal >= 0 ? "Positivo" : "Negativo"}
                      size="small"
                      sx={{
                        mt: 1,
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "white",
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    height: "100%",
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${alpha(theme.palette.secondary.main, 0.8)})`,
                  }}
                >
                  <CardContent sx={{ color: "white" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <ReceiptIcon sx={{ fontSize: 40 }} />
                      <Tooltip title="Información sobre la actividad de transacciones">
                        <IconButton size="small" sx={{ color: "white" }}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Actividad
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {analytics.totalTransacciones}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      En {analytics.diasConMovimientos} días
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tarjetas de información adicional */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <BarChartIcon color="primary" />
                      Día con Mayor Actividad
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatearFechaCompleta(analytics.diaConMayorMovimiento.fecha)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Ventas del día:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="success.main">
                        {formatCurrency(analytics.diaConMayorMovimiento.ventas)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Gastos del día:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="error.main">
                        {formatCurrency(analytics.diaConMayorMovimiento.gastos)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Movimientos:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {analytics.diaConMayorMovimiento.transacciones}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AttachMoneyIcon color="primary" />
                      Indicadores
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Ratio Ventas/Gastos:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {analytics.totalGastos > 0
                          ? `${(analytics.totalVentas / analytics.totalGastos).toFixed(2)}x`
                          : "Sin gastos"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Venta promedio por movimiento:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="success.main">
                        {(() => {
                          const movConVentas = movimientos.filter((movimiento) => esVenta(movimiento)).length
                          return movConVentas > 0 ? formatCurrency(analytics.totalVentas / movConVentas) : "—"
                        })()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Gasto promedio por movimiento:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="error.main">
                        {(() => {
                          const movConGastos = movimientos.filter((movimiento) => esGasto(movimiento)).length
                          return movConGastos > 0 ? formatCurrency(analytics.totalGastos / movConGastos) : "—"
                        })()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Movimientos por día (período):
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {(() => {
                          const diasPeriodo = startDate && endDate
                            ? Math.max(1, dayjs(endDate).diff(dayjs(startDate), "day") + 1)
                            : analytics.diasConMovimientos
                          return (analytics.totalTransacciones / diasPeriodo).toFixed(1)
                        })()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Gráficos */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Gráfico de distribución por tipo de transacción */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: 400 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PieChartIcon color="primary" />
                      Distribución por Tipo de Transacción
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Muestra cómo se distribuye el dinero según el tipo de operación
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          labelLine={false}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [formatCurrency(value), name]}
                          contentStyle={{
                            borderRadius: 8,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Gráfico de evolución temporal */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: 400 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ShowChartIcon color="primary" />
                      Evolución Temporal
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Tendencia de ventas, gastos y balance neto a lo largo del tiempo
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={lineData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis tickFormatter={(value) => {
                          const abs = Math.abs(value)
                          if (abs >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
                          if (abs >= 1000) return `$${(value / 1000).toFixed(0)}k`
                          return `$${value.toFixed(0)}`
                        }} />
                        <RechartsTooltip
                          formatter={(value, name) => [formatCurrency(value), name]}
                          contentStyle={{
                            borderRadius: 8,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="ventas"
                          stroke={theme.palette.success.main}
                          strokeWidth={3}
                          name="Ventas"
                        />
                        <Line
                          type="monotone"
                          dataKey="gastos"
                          stroke={theme.palette.error.main}
                          strokeWidth={3}
                          name="Gastos"
                        />
                        <Line
                          type="monotone"
                          dataKey="neto"
                          stroke={theme.palette.info.main}
                          strokeWidth={3}
                          name="Neto"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Gráfico de caja neta por fecha (mejorado) */}
              <Grid item xs={12}>
                <Card sx={{ height: 400 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <BarChartIcon color="primary" />
                      Flujo de Caja Neto Diario
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Balance neto de cada día (positivo = más ventas que gastos, negativo = más gastos que ventas)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={composedData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis tickFormatter={(value) => {
                          const abs = Math.abs(value)
                          if (abs >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
                          if (abs >= 1000) return `$${(value / 1000).toFixed(0)}k`
                          return `$${value.toFixed(0)}`
                        }} />
                        <RechartsTooltip
                          formatter={(value) => [formatCurrency(value), "Monto Neto"]}
                          contentStyle={{
                            borderRadius: 8,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar dataKey="montoNeto" radius={[4, 4, 0, 0]}>
                          {composedData.map((entry, index) => (
                            <Cell
                              key={`bar-${index}`}
                              fill={entry.montoNeto >= 0 ? theme.palette.success.main : theme.palette.error.main}
                            />
                          ))}
                        </Bar>
                        <Line
                          type="monotone"
                          dataKey="montoNeto"
                          stroke={theme.palette.primary.main}
                          strokeWidth={2}
                          dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabla de resumen por tipo de transacción */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumen Detallado por Tipo de Transacción
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Análisis completo de cada tipo de operación incluida en analíticos
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {Object.entries(analytics.porTipoTransaccion).map(([tipo, datos], index) => (
                    <Grid item xs={12} md={6} lg={4} key={tipo}>
                      <Paper
                        sx={{
                          p: 2,
                          borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                          backgroundColor: alpha(COLORS[index % COLORS.length], 0.05),
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          {tipo}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Ventas:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {formatCurrency(datos.ventas)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Gastos:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium" color="error.main">
                            {formatCurrency(datos.gastos)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Transacciones:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {datos.cantidad}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" fontWeight="bold">
                            Neto:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color={datos.ventas - datos.gastos >= 0 ? "success.main" : "error.main"}>
                            {formatCurrency(datos.ventas - datos.gastos)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </>
        )}

        {!analytics && !loading && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Selecciona los filtros y presiona "Filtrar" para ver el análisis financiero
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Podrás visualizar ventas, gastos, tendencias y análisis detallados del período
            </Typography>
          </Paper>
        )}
      </Box>
    </ThemeProvider>
  )
}

export default CajaComponent
